import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';

import { useFlags } from '@project44/feature-flags';
import {
  Icon,
  Provider,
  reactIcons,
  Separator,
  SideNavigation,
  SideNavigationContent,
  SideNavigationFooter,
  SideNavigationHeader,
  SideNavigationMenu,
} from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';
import { setSidebarIsOpen, useTracking, useUser } from '@project44/ui-utils';

import {
  MOVEMENT_LINKS,
  MOVEMENT_LINKS_analyticsNavAmi1298,
  MOVEMENT_LINKS_WITH_UPSELL,
  UPSELL_LINKS,
} from '../../constants';
import {
  Analyze,
  AppointmentsUpsell,
  Dashboard,
  EcommerceUpsell,
  FacilitiesUpsell,
  Initiate,
  Movement,
  Network,
  P44OS,
  Track,
} from '../../icons';
import { theme } from '../../theme';
import { getNavigationLinks } from '../../utils';
import SideBarMenuGroup from './SideBarMenuGroup';
import { SideBarMenuItem } from './SideBarMenuItem';

const icons: Record<string, React.ElementType> = {
  analyze: Analyze,
  dashboard: Dashboard,
  initiate: Initiate,
  track: Track,
  network: Network,
  p44os: P44OS,
  ecommerce_upsell: EcommerceUpsell,
  facilities_upsell: FacilitiesUpsell,
  appointments_upsell: AppointmentsUpsell,
};

type SideBarProps = {
  toggleTenantSwitcherComponent: () => void;
};

export function SideBar({ toggleTenantSwitcherComponent }: SideBarProps) {
  const { data: user } = useUser({ skipRedirectOn401: true });
  const { t } = useTranslation();
  const flags = useFlags();
  const tracker = useTracking();

  const fullName = user?.getFullName();
  const initials = user?.getTenantInitials();

  const [isOpen, setIsOpen] = useState(false);

  const setIsOpenState = (val: boolean) => {
    setIsOpen(val);
    setSidebarIsOpen(val);
  };

  const navigationLinks = useMemo(() => {
    let links = flags?.analyticsNavAmi1298
      ? MOVEMENT_LINKS_analyticsNavAmi1298
      : MOVEMENT_LINKS;
    if (flags?.featureVoc1382) {
      links = MOVEMENT_LINKS_WITH_UPSELL;
    }

    const navigationLinks = getNavigationLinks({ user, flags, links });

    return navigationLinks.map(({ iconName, label, url, subNavigation }) => ({
      id: iconName,
      label,
      url,
      subNavigation,
      IconComponent: icons[iconName],
      labelString: t(label),
    }));
  }, [flags, t, user]);

  const upsellLinks = useMemo(
    () =>
      UPSELL_LINKS.map(({ iconName, label, url, getPageUrl }) => {
        const pageUrl = getPageUrl({ user });
        return {
          id: iconName,
          label,
          url: pageUrl,
          IconComponent: icons[iconName],
          labelString: t(label),
          external: url !== pageUrl,
        };
      }),
    [t, user],
  );

  const handleMenuOpenChange = useCallback(
    (open: boolean) => {
      setIsOpenState(open);
      tracker.track({
        eventName: `side-nav.menu.${open ? 'expanded' : 'contracted'}`,
        properties: {
          menuId: 'menu-header',
        },
      });
    },
    [tracker],
  );

  const handleMenuGroupClick = useCallback(
    (event, menuId: string) => {
      if (!isOpen) {
        setIsOpenState(true);
        tracker.track({
          eventName: 'side-nav.menu.expanded',
          properties: { menuId },
        });
      }
    },
    [isOpen, tracker],
  );

  const shouldRenderFooter = !!user;

  return (
    <Provider className={theme.className} css={{ height: '100%' }}>
      <SideNavigation
        isOpen={isOpen}
        css={{
          height: '100%',
          width: isOpen ? 240 : 56,
        }}
        onOpenChange={handleMenuOpenChange}
      >
        <SideNavigationHeader
          css={{
            '.manifest-navigation-header__button': { position: 'absolute' },
          }}
          logo={<Movement />}
        />
        <SideNavigationContent>
          {user && (
            <SideNavigationMenu>
              {navigationLinks.map(
                ({
                  id,
                  label,
                  url,
                  subNavigation,
                  IconComponent,
                  labelString,
                }) =>
                  subNavigation ? (
                    <SideBarMenuGroup
                      key={label}
                      menuId={id}
                      url={url}
                      label={labelString}
                      startIcon={<IconComponent label={labelString} />}
                      isExpanded={isOpen}
                      onClick={handleMenuGroupClick}
                    >
                      {subNavigation.map(
                        ({
                          iconName: subId,
                          label: subLabel,
                          url: subUrl,
                          externalIcon,
                        }) => (
                          <SideBarMenuItem
                            key={subLabel}
                            menuId={subId}
                            url={subUrl}
                            label={t(subLabel)}
                            labelProps={
                              externalIcon
                                ? {
                                    // For keeping call_made icon near label
                                    css: { flexGrow: 'initial !important' },
                                  }
                                : undefined
                            }
                            endIcon={
                              externalIcon ? (
                                <Icon
                                  css={{ fontSize: '$small' }}
                                  icon="call_made"
                                />
                              ) : undefined
                            }
                          />
                        ),
                      )}
                    </SideBarMenuGroup>
                  ) : (
                    <SideBarMenuItem
                      key={label}
                      menuId={id}
                      url={url}
                      label={labelString}
                      startIcon={<IconComponent label={labelString} />}
                    />
                  ),
              )}
              {flags?.featureVoc1382 && (
                <>
                  <Separator css={{ margin: '$medium 0' }} />
                  {upsellLinks.map(
                    ({
                      id,
                      label,
                      url,
                      IconComponent,
                      labelString,
                      external,
                    }) => (
                      <SideBarMenuItem
                        key={label}
                        menuId={id}
                        url={url}
                        external={external}
                        label={labelString}
                        startIcon={<IconComponent label={labelString} />}
                        endIcon={
                          external ? <reactIcons.ArrowUpAndRight /> : undefined
                        }
                      />
                    ),
                  )}
                </>
              )}
            </SideNavigationMenu>
          )}
          <SideNavigationMenu>
            <SideBarMenuItem
              menuId="settings"
              url="/settings"
              label={t('navigation.settings.title')}
              labelProps={{ css: { fontWeight: '$regular' } }}
              startIcon={
                <reactIcons.Settings
                  aria-label={t('navigation.settings.title')}
                  data-testid="navigation-icon-admin"
                />
              }
              css={{ color: '$palette-indigo-100' }}
            />
          </SideNavigationMenu>
        </SideNavigationContent>
        {shouldRenderFooter && (
          <SideNavigationFooter
            onClick={toggleTenantSwitcherComponent}
            avatarProps={{ fallback: initials }}
            css={{
              // TODO: move into manifest
              '.manifest-navigation-footer__content': {
                overflow: 'hidden',
              },

              '.manifest-navigation-footer__title': {
                minWidth: 0,
                overflow: 'hidden',
                textAlign: 'initial',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
              },
            }}
            title={user?.companyName}
            subTitle={fullName}
            data-testid="sidebar-ui-avatar"
          />
        )}
      </SideNavigation>
    </Provider>
  );
}
